import { onlineManager } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import type { UserMinimalBase } from 'sdk';
import { EntityAvatar } from '~/modules/common/entity-avatar';
import { toaster } from '~/modules/common/toaster/toaster';
import { cn } from '~/utils/cn';
import { Button } from '../ui/button';

interface BaseProps {
  tabIndex: number;
  compactable?: boolean;
  className?: string;
  /** When true, render avatar + name without the button/navigate behavior (e.g. in public layouts where the user sheet is not mounted). */
  readOnly?: boolean;
}

// DataGrid sets data-is-compact on its root/merged slots; keep the name accessible while hiding it visually.
const compactUserNameClass = 'in-data-[is-compact=true]:sr-only';

export function UserCell({ user, tabIndex, compactable, className, readOnly }: BaseProps & { user: UserMinimalBase }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const cellRef = useRef<HTMLButtonElement | null>(null);

  // While compacted the name is visually hidden, so the compact-scoped table tooltip shows it.
  const compactTooltip = compactable && user.name ? { 'data-tooltip': 'compact', 'data-tooltip-content': user.name } : undefined;

  if (readOnly) {
    return (
      <div className={cn('flex items-center gap-2', className)} {...compactTooltip}>
        <EntityAvatar type="user" className="size-8" id={user.id} name={user.name} url={user.thumbnailUrl} />
        <span className={cn('truncate', { [compactUserNameClass]: compactable })}>{user.name || '-'}</span>
      </div>
    );
  }

  return (
    <Button
      ref={cellRef}
      variant="cell"
      size="cell"
      className={className}
      tabIndex={tabIndex}
      draggable={false}
      {...compactTooltip}
      onClick={(e) => {
        if (!onlineManager.isOnline()) {
          e.preventDefault();
          return toaster.warning(t('c:action.offline.text'));
        }
        if (e.metaKey || e.ctrlKey) return;
        e.preventDefault();

        navigate({ to: '.', replace: false, resetScroll: false, search: (prev) => ({ ...prev, userSheetId: user.id }) });
      }}
    >
      <EntityAvatar type="user" className="group-active/cell-button:press size-8" id={user.id} name={user.name} url={user.thumbnailUrl} />
      <span
        className={cn(
          'group-active/cell-button:press truncate decoration-foreground/20 underline-offset-3 group-hover/cell-button:underline group-active/cell-button:decoration-foreground/50',
          { [compactUserNameClass]: compactable },
        )}
      >
        {user.name || '-'}
      </span>
    </Button>
  );
}
